import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Video, RotateCw, Sparkles, Upload, Check } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { useRole } from "@/hooks/useRole";
import { VoicePicker } from "@/components/VoicePicker";

const KINDS = [
  { key: "broll", label: "B-roll + voiceover", desc: "Cinematic footage narrated by a chosen voice" },
  { key: "scene", label: "Talking scene", desc: "A person speaking to camera with native audio" },
  { key: "avatar", label: "Talking avatar", desc: "Lip-sync a photo to a voiceover" },
];

export default function AIVideo() {
  const api = useApi();
  const { canManageContent } = useRole();
  const [options, setOptions] = useState(null);
  const [brief, setBrief] = useState("");
  const [kind, setKind] = useState("broll");
  const [voiceId, setVoiceId] = useState("");
  const [delivery, setDelivery] = useState("");
  const [pace, setPace] = useState(1.0);
  const [emotionTag, setEmotionTag] = useState(null);
  const [duration, setDuration] = useState(10);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [result, setResult] = useState(null);
  const [reviewPlatform, setReviewPlatform] = useState("instagram");
  const [sendingReview, setSendingReview] = useState(false);
  const [sentToReview, setSentToReview] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/admin/reels/options").then((r) => {
      setOptions(r.data);
      setVoiceId(r.data.default_voice);
      setDelivery(r.data.default_delivery);
      setDuration(r.data.default_duration);
    }).catch(() => toast.error("Couldn't load video options"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => clearInterval(pollRef.current), []);

  if (!canManageContent) {
    return <div className="text-[#7A9090] p-8" data-testid="ai-video-no-access">You don't have access to AI Video.</div>;
  }

  const statusMsg = elapsedSec < 20 ? "Writing the script…"
    : elapsedSec < 200 ? "Rendering the video — this usually takes a few minutes…"
    : "Adding the voiceover and finishing up — almost there…";

  const onImageSelected = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImage(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const pollJob = (jobId) => {
    let elapsed = 0;
    pollRef.current = setInterval(async () => {
      elapsed += 4;
      setElapsedSec(elapsed);
      try {
        const r = await api.get("/admin/reels/status", { params: { job_id: jobId } });
        if (r.data.status === "done") {
          clearInterval(pollRef.current);
          setResult({ ...r.data, job_id: jobId });
          setGenerating(false);
          setSentToReview(false);
          toast.success("Your reel is ready — saved to the Media Library");
        } else if (r.data.status === "error") {
          clearInterval(pollRef.current);
          setGenerating(false);
          toast.error(r.data.error || "Video generation failed");
        } else if (r.data.status === "canceled") {
          clearInterval(pollRef.current);
          setGenerating(false);
          toast("Rendering stopped — no video was saved.");
        } else if (elapsed > 480) {
          clearInterval(pollRef.current);
          setGenerating(false);
          toast.error("Still rendering after 8 minutes — it will still land in your Media Library when it finishes, check back shortly.");
        }
      } catch (e) { /* transient — keep polling */ }
    }, 4000);
  };

  const generate = async () => {
    if (!brief.trim()) { toast.error("Describe what the reel should be about"); return; }
    if (kind === "avatar" && !image) { toast.error("Upload a photo for the talking avatar"); return; }
    setGenerating(true);
    setResult(null);
    setElapsedSec(0);
    try {
      let jobId;
      if (kind === "avatar") {
        const fd = new FormData();
        fd.append("brief", brief);
        fd.append("voice_id", voiceId);
        fd.append("delivery", delivery);
        fd.append("duration", duration);
        fd.append("pace", pace);
        if (emotionTag) fd.append("emotion_tag", emotionTag);
        fd.append("image", image);
        const r = await api.post("/admin/reels/generate-avatar", fd);
        jobId = r.data.job_id;
      } else {
        const r = await api.post("/admin/reels/generate", { brief, kind, voice_id: voiceId, delivery, duration, pace, emotion_tag: emotionTag });
        jobId = r.data.job_id;
      }
      setCurrentJobId(jobId);
      pollJob(jobId);
    } catch (e) {
      setGenerating(false);
      toast.error(e?.response?.data?.detail || "Couldn't start video generation");
    }
  };

  const stopGeneration = async () => {
    if (!currentJobId) return;
    setStopping(true);
    try {
      await api.post("/admin/reels/cancel", { job_id: currentJobId });
      clearInterval(pollRef.current);
      setGenerating(false);
      toast("Rendering stopped — no video was saved.");
    } catch (e) {
      toast.error("Couldn't stop it — it may already be finishing.");
    } finally {
      setStopping(false);
    }
  };

  const sendToReview = async () => {
    if (!result?.job_id) return;
    setSendingReview(true);
    try {
      await api.post("/admin/reels/send-to-review", { job_id: result.job_id, platform: reviewPlatform });
      setSentToReview(true);
      toast.success("Sent to the Approval Queue (Needs review)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send to review");
    } finally { setSendingReview(false); }
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="ai-video-container">
      <div>
        <h1 className="text-4xl font-bold text-[#3C4048] mb-2 tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>AI Video</h1>
        <p className="text-[#7A9090]">Generate a cinematic 9:16 Reel — on-brand and narrated, ready for Instagram/TikTok. It saves straight to your Media Library, then send it to the Approval Queue when you're happy with it.</p>
      </div>

      {options && !options.configured && (
        <div className="bg-[#fef2f2] border border-[#fecaca] rounded-[12px] p-4 text-[#b91c1c] text-sm" data-testid="ai-video-not-configured">
          Video generation isn't configured on the server yet — ask your admin to set FAL_KEY.
        </div>
      )}

      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6 space-y-5">
        <div>
          <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Reel type</Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {KINDS.map((k) => (
              <button key={k.key} type="button" data-testid={`video-kind-${k.key}`} onClick={() => setKind(k.key)}
                className={`text-left p-3 rounded-[10px] border transition-colors ${kind === k.key ? "bg-[#eef7f8] border-[#5FA8B4]" : "bg-white border-[#e5e7eb] hover:border-[#5FA8B4]"}`}>
                <p className="text-[#3C4048] text-sm font-semibold">{k.label}</p>
                <p className="text-[#7A9090] text-xs mt-0.5">{k.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">What's the reel about?</Label>
          <Textarea data-testid="video-brief" value={brief} onChange={(e) => setBrief(e.target.value)}
            placeholder="e.g. Our new lymphatic drainage massage service, calming and restorative"
            className="border-[#e5e7eb] text-[#3C4048] min-h-[90px]" />
        </div>

        {kind === "avatar" && (
          <div>
            <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Presenter photo</Label>
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onImageSelected} data-testid="video-avatar-file-input" />
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-2 border-[#e5e7eb] text-[#3C4048]" data-testid="video-avatar-upload-btn">
                <Upload size={16} /> {image ? "Change photo" : "Upload photo"}
              </Button>
              {imagePreview && <img src={imagePreview} alt="Presenter" className="w-14 h-14 rounded-[10px] object-cover border border-[#e5e7eb]" />}
            </div>
          </div>
        )}

        {options && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {kind !== "scene" && (
              <div>
                <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Voice & performance</Label>
                <VoicePicker
                  api={api} previewEndpoint="/admin/reels/preview-voice"
                  voices={options.voices} deliveries={options.deliveries} emotionTags={options.emotion_tags}
                  voiceId={voiceId} onVoiceChange={setVoiceId}
                  delivery={delivery} onDeliveryChange={setDelivery}
                  pace={pace} onPaceChange={setPace}
                  emotionTag={emotionTag} onEmotionTagChange={setEmotionTag}
                />
              </div>
            )}
            {kind === "scene" && (
              <div>
                <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Delivery</Label>
                <select data-testid="video-scene-delivery-select" value={delivery} onChange={(e) => setDelivery(e.target.value)} className="w-full border border-[#e5e7eb] rounded-[10px] p-2 text-sm text-[#3C4048] bg-white">
                  {options.deliveries.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                </select>
              </div>
            )}
            <div>
              <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Duration</Label>
              <select data-testid="video-duration-select" value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="w-full border border-[#e5e7eb] rounded-[10px] p-2 text-sm text-[#3C4048] bg-white">
                {options.durations.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
            </div>
          </div>
        )}

        <Button data-testid="video-generate-btn" onClick={generate} disabled={generating || (options && !options.configured)} className="bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white gap-2 rounded-[12px]">
          <Video size={18} /> {generating ? "Generating…" : "Generate reel"}
        </Button>
      </div>

      {generating && (
        <div className="bg-[#f0f7f8] border border-[#d7e6e9] rounded-[12px] p-5 flex items-center justify-between gap-3" data-testid="video-generating-status">
          <div className="flex items-center gap-3">
            <RotateCw size={18} className="animate-spin text-[#3A7A8C]" />
            <p className="text-[#3A7A8C] text-sm font-medium">{statusMsg} ({elapsedSec}s)</p>
          </div>
          <Button size="sm" variant="outline" onClick={stopGeneration} disabled={stopping}
            className="h-8 text-xs border-[#3A7A8C] text-[#3A7A8C] hover:bg-[#3A7A8C] hover:text-white rounded-[8px] shrink-0"
            data-testid="video-stop-btn">
            {stopping ? "Stopping…" : "Stop rendering"}
          </Button>
        </div>
      )}

      {result && (
        <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6 flex flex-col md:flex-row gap-6" data-testid="video-result">
          <video src={result.video_url} controls className="w-full md:w-64 rounded-[10px] bg-black aspect-[9/16] object-cover" data-testid="video-result-player" />
          <div className="flex-1 space-y-2">
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#3A7A8C] text-white text-[10px] font-semibold"><Sparkles size={11} /> {result.title || "Reel"}</div>
            {result.script && <p className="text-[#3C4048] text-sm whitespace-pre-line" data-testid="video-result-script">{result.script}</p>}
            {result.cta && <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#eef7f8] text-[#3A7A8C] text-xs font-semibold" data-testid="video-result-cta">{result.cta}</div>}
            <p className="text-[#7A9090] text-xs flex items-center gap-1.5 pt-2"><Check size={13} /> Saved to Media Library.</p>
            <div className="flex items-center gap-2 pt-2">
              <select data-testid="video-review-platform" value={reviewPlatform} onChange={(e) => setReviewPlatform(e.target.value)}
                disabled={sentToReview} className="border border-[#e5e7eb] rounded-[10px] p-2 text-sm text-[#3C4048] bg-white">
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
                <option value="linkedin">LinkedIn</option>
              </select>
              <Button data-testid="video-send-review-btn" onClick={sendToReview} disabled={sendingReview || sentToReview}
                className="bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white gap-1.5 rounded-[10px] h-9 text-sm">
                {sentToReview ? <><Check size={15} /> Sent to review</> : "Send to Approval Queue"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
